import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { WEBSITE_VERSION } from "@/lib/version";
import { EXPECTED_BACKEND } from "@/components/admin/SystemStatus";

/**
 * Public, read-only check page (dartmyvenue.com/status). Shows which website build this browser is
 * running and whether the server behind it is the updated one. No private data is shown.
 */
const Row = ({ state, title, children, testId }) => (
  <div className="flex gap-4 py-4 border-t border-[color:var(--line)]" data-testid={testId} data-state={state}>
    <span className={`mt-1.5 w-3 h-3 rounded-full shrink-0 ${state === "ok" ? "bg-emerald-500" : state === "warn" ? "bg-amber-400" : state === "bad" ? "bg-rose-500" : "bg-neutral-300"}`} aria-hidden/>
    <div className="min-w-0">
      <div className="font-semibold text-[16px] text-[color:var(--ink)]">{title}</div>
      <div className="text-[15px] text-[color:var(--ink-2)] mt-0.5 break-words">{children}</div>
    </div>
  </div>
);

export default function Status() {
  const [h, setH] = useState(null);
  const [bundle, setBundle] = useState({ mine: null, latest: null });

  useEffect(() => {
    api.get("/health").then((r) => setH(r.data || {})).catch((e) => setH({ error: e?.response?.status || "no answer" }));
    const mine = Array.from(document.scripts).map((x) => x.src).find((x) => /\/static\/js\/main\.[\w]+\.js/.test(x));
    fetch(`/asset-manifest.json?t=${Date.now()}`, { cache: "no-store" }).then((r) => (r.ok ? r.json() : null))
      .then((m) => setBundle({ mine: mine ? new URL(mine).pathname : null, latest: m?.files?.["main.js"] || null })).catch(() => {});
  }, []);

  const stale = bundle.mine && bundle.latest && bundle.mine !== bundle.latest;
  const serverOk = h && !h.error && h.version && h.version >= EXPECTED_BACKEND;
  const serverOld = h && (h.error || !h.version || h.version < EXPECTED_BACKEND);

  const refresh = async () => {
    try { if (window.caches) await Promise.all((await caches.keys()).map((k) => caches.delete(k))); } catch {}
    try { if (navigator.serviceWorker) await Promise.all((await navigator.serviceWorker.getRegistrations()).map((r) => r.unregister())); } catch {}
    window.location.replace(`/?refresh=${Date.now()}`);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 md:py-16" data-testid="status-page">
      <h1 className="font-display text-[38px] md:text-[48px] leading-tight text-[color:var(--maroon)]">Site status</h1>
      <p className="mt-3 text-[16px] text-[color:var(--ink-2)]">A quick check of the website and the server behind it.</p>

      <div className="mt-8 border-b border-[color:var(--line)]">
        <Row state={stale ? "bad" : "ok"} title="Website in this browser" testId="status-website">
          Version {WEBSITE_VERSION}.{" "}
          {stale ? <>A newer website is on the server, but this browser is showing an older saved copy. <button onClick={refresh} className="link-strong">Load the latest version</button></>
            : bundle.latest ? "This is the latest version on the server." : ""}
        </Row>
        <Row state={!h ? "wait" : serverOk ? "ok" : "bad"} title="Server" testId="status-server">
          {!h ? "Checking…" : h.error ? `The server didn't answer the health check (${h.error}). It is not running, or it is the old version.`
            : serverOk ? `Updated (version ${h.version}).`
            : <>This is the <b>old server</b>{h.version ? ` (version ${h.version})` : " (no version number)"}. It needs version {EXPECTED_BACKEND}. Until it is updated, photo uploads, adding staff, newsletter sign-ups and online payments won't work.</>}
        </Row>
        <Row state={!h ? "wait" : serverOld ? "bad" : h.razorpay ? "ok" : "warn"} title="Online payments (Razorpay)" testId="status-payments">
          {!h ? "Checking…" : serverOld ? "Not available on the old server."
            : h.razorpay ? `On, ${h.razorpay_mode || ""} mode.${h.razorpay_mode === "test" ? " Test keys take no real money." : ""}`
            : !h.razorpay_key_id_set ? "Off: the Key ID is missing." : "Off: the Key Secret is missing."}
        </Row>
        <Row state={!h ? "wait" : serverOld ? "bad" : h.uploads_writable ? "ok" : "bad"} title="Photo & video uploads" testId="status-uploads">
          {!h ? "Checking…" : serverOld ? "Not available on the old server." : h.uploads_writable ? "Ready." : "The server can't save files. Check the uploads folder permissions."}
        </Row>
      </div>
    </div>
  );
}
